import type {
  ButtonProps
} from '@chakra-ui/react'
import {
  Box,
  Button,
  Container,
  Flex,
  forwardRef,
  Grid,
  HStack,
  Image,
  useBreakpointValue
} from '@chakra-ui/react'
import { Product } from 'BrokerTsLib/src/enums'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Information } from '@/components/Menu/Information'
import { MenuItem } from '@/components/Menu/MenuItem'
import { Poleas } from '@/components/Menu/Poleas'
import { useAuth } from '@/context/AuthContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'

import { AppSwitcher } from '../../components/AppSwitcher'
import { Menu } from '../../components/Menu'
import { User } from '../../components/Menu/User'
import menuToggle from '../../images/menuToggle.svg'
import { NewInsuranceButton } from './NewInsuranceButton'

export const Header = () => {
  const { user } = useAuth()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isMobile = useBreakpointValue({ base: true, md: false })

  const dailyPov = user?.dailyPov ?? false

  return (
    <Box as="header" id="header" sx={{ bgColor: 'white' }}>
      <Container py={4}>
        <Grid
          sx={{
            w: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            gridTemplateColumns: {
              base: '1fr',
              md: '250px 2rem 1fr',
            }
          }}
        >
          <Flex sx={{ w: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <AppSwitcher />

            <HStack gap={4}>
              {isMobile && <NewInsurance />}

              <Button
                variant="link"
                sx={{
                  display: {
                    base: 'block',
                    md: 'none',
                  },
                  px: 2,
                }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                w={8}
              >
                <Image
                  src={menuToggle}
                  alt="Menu"
                  aria-label="Menu"
                  sx={{ w: 8, h: 8, color: 'primary.500' }}
                />
              </Button>
            </HStack>
          </Flex>

          <Box />

          <Flex
            sx={{
              display: {
                base: isMenuOpen ? 'flex' : 'none',
                md: 'flex',
              },
              w: { base: '100%' },
              pt: { base: 4, md: 0 },
              alignItems: {
                base: 'flex-start',
                md: 'center',
              },
              gap: {
                base: 4,
              },
              justifyContent: 'space-between',
              flexDirection: { base: 'column', md: 'row' },
              flexWrap: { base: 'wrap', md: 'nowrap' },
            }}
          >
            {!isMobile && <NewInsurance />}

            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              gap={{ base: 2, lg: 4 }}
              flexWrap="wrap"
              justifyContent="end"
              w="full"
              alignItems={'end'}
            >
              <Menu />

              {dailyPov && (
                <Flex
                  sx={{
                    gap: {
                      base: 4,
                      lg: 8,
                    },
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <MenuItem href={'/shortInsurances'} disabled={false}>
                    Denní POV
                  </MenuItem>
                </Flex>
              )}

              {user?.poleas?.poleasEnabled && (
                <Poleas />
              )}

              <Information />

              <User />

            </Flex>
          </Flex>
        </Grid>
      </Container>
    </Box>
  )
}

const NewInsurance = () => {
  const { lastProduct: product, lastApp } = useApp()
  const { user } = useAuth()

  let allowNewInsurance = true
  const allowNewExtraInsurance = !!(!!user?.vehicleExtraRights && lastApp?.app && ([Apps.vehicle, Apps.extra, Apps.shortInsurance] as Apps[]).includes(lastApp.app))
  const newDailyPov = user?.newDailyPov ?? false
  const enableContractReplacement = !!(
    (user?.enableContractReplacement ?? false) &&
    lastApp?.app &&
    ([Apps.property, Apps.vehicle, Apps.extra, Apps.shortInsurance] as Apps[]).includes(lastApp?.app)
  )

  if (
    user?.autorizacePpz &&
    (!user.autorizacePpz.hasAuthorization ||
      !user.autorizacePpz.authorizedFrom ||
      !user.autorizacePpz.authorizedTo ||
      user.autorizacePpz.authorizedFrom > new Date() ||
      user.autorizacePpz.authorizedTo < new Date())
  ) {
    allowNewInsurance = false
  }

  let step1Path
  let liabilityInsurance = false
  switch (product) {
    case Product.TravelInsurance:
      step1Path = '/travel/steps/1'
      break
    case Product.PropertyInsurance:
    case Product.LiabilityInsurance:
      liabilityInsurance = user?.liabilityRights ?? false
      step1Path = '/property/steps/1'
      break
    case Product.VehicleLeasingInsurance:
      step1Path = '/leasing/steps/1'
      break
    case Product.FleetInsurance:
      step1Path = '/fleet/steps/1'
      break
    case Product.VehicleInsurance:
    default:
      step1Path = '/steps/1'
      break
  }

  if (allowNewInsurance && product !== Product.VehicleLeasingInsurance) {
    return <NewInsuranceButton
      allowNewExtraInsurance={allowNewExtraInsurance}
      enableContractReplacement={enableContractReplacement}
      newDailyPov={newDailyPov}
      step1Path={step1Path}
      liabilityInsurance={liabilityInsurance}
    />
  }

  if (!allowNewInsurance)
    return <Button
      as={Link}
      to={'/ppzAuthorization/list'}
      colorScheme="red"
      sx={{
        w: {
          base: '100%',
          md: 'fit-content',
        },
        px: {
          base: 2,
          lg: 6,
        },
        fontSize: {
          base: 'xs',
          lg: 'sm',
        },
        whiteSpace: 'nowrap',
      }}
    >
      Neplatná autorizace PPZ
    </Button>
}

export const StyledMenuItem = forwardRef(function StyledMenuItem(
  {
    to,
    onClick,
    children,
    ...rest
  }: {
    to: string
    onClick?: () => void
    children: React.ReactNode
  } & ButtonProps,
  ref,
) {
  return (
    <Button
      ref={ref}
      as={Link}
      onClick={onClick}
      to={to}
      justifyContent={'start'}
      w={'100%'}
      colorScheme="primary"
      {...rest}
    >
      {children}
    </Button>
  )
})
